import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { Check } from 'lucide-react'

import { Button, Separator } from '@mdxui/primitives'

import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  cardVariants,
  reducedMotionVariants,
} from '../../lib/motion'

export interface PricingCompactProps {
  heading?: string
  description?: string
  price?: string | number
  priceSuffix?: string
  features?: string[][]
  buttonText?: string
  buttonUrl?: string
  className?: string
}

const defaultFeatures = [
  ['Unlimited', 'Integrations', '24/7 support'],
  ['Live collaborations', 'Unlimited storage', '30-day money back'],
  ['Unlimited members', 'Customization', 'Unlimited users'],
]

const PricingCompact = ({
  heading = 'Pricing',
  description = 'Simple pricing with a free 7 day trial.',
  price = 29,
  priceSuffix = '/mo',
  features = defaultFeatures,
  buttonText = 'Start free trial',
  buttonUrl = '#',
  className,
}: PricingCompactProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const cardAnimation = prefersReducedMotion ? reducedMotionVariants : cardVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container mx-auto">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 text-center">
          <motion.div variants={headerAnimation}>
            <h2 className="text-pretty text-3xl font-semibold lg:text-4xl">{heading}</h2>
            <div className="mx-auto mt-4 max-w-md"><Markdown size="base">{description}</Markdown></div>
          </motion.div>
          <motion.div
            className="mx-auto flex w-full flex-col rounded-[var(--radius)] border border-border p-6 sm:w-fit sm:min-w-80"
            variants={cardAnimation}
          >
            <div className="flex justify-center">
              <span className="text-lg font-semibold">$</span>
              <span className="text-4xl font-semibold lg:text-5xl">{price}</span>
              <span className="self-end text-muted-foreground">{priceSuffix}</span>
            </div>
            <div className="my-6">
              {features.map((featureGroup, idx) => (
                <div key={idx.toString()}>
                  <ul className="flex flex-col gap-3">
                    {featureGroup.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center justify-between gap-2 text-sm font-medium"
                      >
                        {feature} <Check className="inline size-4 shrink-0" />
                      </li>
                    ))}
                  </ul>
                  {idx < features.length - 1 && <Separator className="my-6" />}
                </div>
              ))}
            </div>
            <Button className="w-full" asChild>
              <a href={buttonUrl}>{buttonText}</a>
            </Button>
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}

export { PricingCompact }
